/**
 * Opening stock: what each store held on the day the books start, and at
 * what cost -- typed from the stock sheet, or imported from Excel.
 *
 * Entered per store, in whatever unit the sheet uses (10 CTN at 96.00);
 * the server turns it into base units and a cost per piece. Saving
 * replaces the store's opening stock, as the opening-balance screen does,
 * because this is a statement of position corrected until go-live.
 *
 * The books panel closes the loop: the opening Stock-in-Hand in the
 * accounts is set to the value of this stock, with the difference named
 * in the opening-balance difference account rather than left to tilt the
 * trial balance.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Paper, Skeleton, Stack, Tab, Tabs, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import UploadIcon from '@mui/icons-material/UploadFileOutlined'
import { useTranslation } from 'react-i18next'

import {
  fetchInvItems, fetchOpeningStock, fetchStores, importOpeningStock,
  postOpeningStockToBooks, saveOpeningStock,
} from '../api/endpoints'
import { errorMessage } from '../api/client'
import { P, type InvItem } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { useGuard, useUnsavedChanges } from '../store/UnsavedChanges'
import ScreenTitle from '../components/ScreenTitle'
import { Figure, FigureGrid } from '../reports/SummaryFigures'
import { useBi } from '../components/Bi'
import { c, fmtMoney } from '../theme'
import { sanitize } from '../vouchers/money'
import ImportDialog from './ImportDialog'

interface Line { key: number; item_code: string; qty: string; unit: string; unit_cost: string }

let nextKey = 1
const blank = (): Line => ({ key: nextKey++, item_code: '', qty: '', unit: '', unit_cost: '' })

export default function OpeningStockPage() {
  const { t, i18n } = useTranslation()
  const bi = useBi()
  const qc = useQueryClient()
  const { can } = useAuth()
  const ar = i18n.language === 'ar'
  const mayPost = can(P.reportFinancial) && can(P.adminPeriods)
  const guard = useGuard()

  const stores = useQuery({ queryKey: ['inv-stores'], queryFn: fetchStores })
  const items = useQuery({ queryKey: ['inv-items', '', '', 'active'],
                           queryFn: () => fetchInvItems({ active: true }) })
  const [storeId, setStoreId] = useState<number | null>(null)
  useEffect(() => {
    if (storeId === null && stores.data?.length) setStoreId(stores.data[0].id)
  }, [stores.data, storeId])

  const opening = useQuery({
    enabled: storeId !== null && !!items.data, queryKey: ['inv-opening', storeId],
    queryFn: () => fetchOpeningStock(storeId!),
  })

  const itemsByCode = useMemo(() => new Map((items.data?.items ?? []).map((i) => [i.code, i])),
    [items.data])
  const byCode = itemsByCode
  const [lines, setLines] = useState<Line[]>([blank()])
  const [loadedFor, setLoadedFor] = useState<string>('')
  // The server keeps base units (2,880 PCS at 1.60); show the line back in
  // the biggest pack it divides into exactly (120 CTN at 38.40), which is
  // how it was counted and typed.
  const serverLines = useMemo<Line[]>(() => (opening.data?.lines ?? []).map((l) => {
    const it = itemsByCode.get(l.item_code)
    const q = Number(l.qty), cost = Number(l.unit_cost)
    const pack = it?.units.filter((u) => Number(u.factor) > 1 && q % Number(u.factor) === 0)
      .sort((a, b) => Number(b.factor) - Number(a.factor))[0]
    const f = pack ? Number(pack.factor) : 1
    return { key: nextKey++, item_code: l.item_code, qty: String(+(q / f).toFixed(3)),
             unit: pack ? pack.unit : (l.unit ?? ''),
             unit_cost: String(+(cost * f).toFixed(4)) }
  }), [opening.data, itemsByCode])
  useEffect(() => {
    if (!opening.data) return
    setLines(serverLines.length ? [...serverLines, blank()] : [blank()])
    setLoadedFor(JSON.stringify(serverLines.map(strip)))
  }, [serverLines, opening.data])

  const filled = lines.filter((l) => l.item_code)
  const dirty = JSON.stringify(filled.map(strip)) !== loadedFor
  useUnsavedChanges(dirty)

  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [importing, setImporting] = useState(false)
  const [confirmPost, setConfirmPost] = useState(false)


  const save = useMutation({
    mutationFn: () => saveOpeningStock(storeId!, filled.map((l) => ({
      item_code: l.item_code, qty: l.qty, unit: l.unit || null, unit_cost: l.unit_cost }))),
    onSuccess: (d) => {
      setError(null); setSaved(true)
      qc.setQueryData(['inv-opening', storeId], d)
      void qc.invalidateQueries({ queryKey: ['inv-items'] })
      void qc.invalidateQueries({ queryKey: ['inv-stores'] })
    },
    onError: (e) => { setSaved(false); setError(errorMessage(e)) },
  })

  const post = useMutation({
    mutationFn: postOpeningStockToBooks,
    onSuccess: () => {
      setConfirmPost(false)
      void qc.invalidateQueries({ queryKey: ['inv-opening'] })
      void qc.invalidateQueries({ queryKey: ['opening'] })
    },
    onError: (e) => { setConfirmPost(false); setError(errorMessage(e)) },
  })

  const setLine = (key: number, patch: Partial<Line>) => {
    setSaved(false)
    setLines((ls) => {
      const next = ls.map((l) => (l.key === key ? { ...l, ...patch } : l))
      // Always one empty line at the end to type into.
      return next[next.length - 1].item_code ? [...next, blank()] : next
    })
  }

  const lineValue = (l: Line) => (Number(l.qty) || 0) * (Number(l.unit_cost) || 0)
  const total = filled.reduce((s, l) => s + lineValue(l), 0)
  const o = opening.data
  const diff = o?.total_value !== undefined && o?.books_value != null
    ? Number(o.total_value) - Number(o.books_value) : 0

  const options = items.data?.items ?? []

  return (
    <Stack spacing={2}>
      <Paper variant="outlined">
        <ScreenTitle titleKey="nav.openingStock" sub={t('inv.openingSub')} right={
          <Button variant="outlined" startIcon={<UploadIcon />} disabled={storeId === null}
            onClick={() => setImporting(true)}>{t('inv.import')}</Button>
        } />

        <Tabs value={storeId ?? false} onChange={(_, v) => guard(() => setStoreId(v))}
          variant="scrollable" allowScrollButtonsMobile
          sx={{ px: 1.5, borderBottom: `1px solid ${c.border}`, minHeight: 40 }}>
          {(stores.data ?? []).filter((s) => s.is_active).map((s) => (
            <Tab key={s.id} value={s.id} sx={{ minHeight: 40 }}
              label={`${(ar && s.name_ar) || s.name_en}`} />
          ))}
        </Tabs>

        {error && <Alert severity="error" sx={{ m: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {saved && !dirty && <Alert severity="success" sx={{ m: 2 }}
          onClose={() => setSaved(false)}>{t('inv.openingSaved')}</Alert>}

        {opening.isLoading || items.isLoading ? (
          <Box sx={{ p: 2 }}><Skeleton /><Skeleton /></Box>
        ) : (
          <Box sx={{ overflowX: 'auto' }}>
            <table className="rpt" style={{ minWidth: 760 }}>
              <thead><tr>
                <th>{t('inv.item')}</th>
                <th className="num" style={{ width: 110 }}>{t('inv.qty')}</th>
                <th style={{ width: 110 }}>{t('inv.unit')}</th>
                <th className="num" style={{ width: 130 }}>{t('inv.costPerUnit')}</th>
                <th className="num" style={{ width: 130 }}>{t('inv.value')}</th>
                <th style={{ width: 44 }} />
              </tr></thead>
              <tbody>
                {lines.map((l, idx) => {
                  const it = byCode.get(l.item_code)
                  return (
                    <tr key={l.key}>
                      <td style={{ padding: '4px 8px' }}>
                        <Autocomplete<InvItem> size="small" options={options}
                          value={it ?? null} autoHighlight
                          getOptionLabel={(o) => `${o.code} — ${(ar && o.name_ar) || o.name_en}`}
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          filterOptions={(opts, s) => {
                            const n = s.inputValue.trim().toLowerCase()
                            return (n ? opts.filter((o) => o.code.toLowerCase().includes(n)
                              || o.name_en.toLowerCase().includes(n) || (o.name_ar ?? '').includes(n)
                              || o.barcode === n || o.units.some((u) => u.barcode === n)) : opts)
                              .slice(0, 50)
                          }}
                          onChange={(_, v) => setLine(l.key, { item_code: v?.code ?? '',
                            unit: v ? (l.unit && (l.unit === v.base_unit
                              || v.units.some((u) => u.unit === l.unit)) ? l.unit : v.base_unit) : '' })}
                          renderInput={(p) => <TextField {...p} placeholder={t('inv.pickItem')}
                            inputProps={{ ...p.inputProps, 'aria-label': t('inv.itemOnLine', { n: idx + 1 }) }} />} />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <TextField size="small" value={l.qty} disabled={!it}
                          inputProps={{ inputMode: 'decimal', className: 'num',
                                        style: { textAlign: 'end' },
                                        'aria-label': t('inv.qtyOnLine', { n: idx + 1 }) }}
                          onChange={(e) => setLine(l.key, { qty: sanitize(e.target.value) })} />
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <TextField select size="small" fullWidth value={it ? (l.unit || it.base_unit) : ''}
                          disabled={!it} onChange={(e) => setLine(l.key, { unit: e.target.value })}>
                          {it && [it.base_unit, ...it.units.map((u) => u.unit)].map((u) => (
                            <MenuItem key={u} value={u}>{u}</MenuItem>))}
                        </TextField>
                      </td>
                      <td style={{ padding: '4px 8px' }}>
                        <TextField size="small" value={l.unit_cost} disabled={!it}
                          inputProps={{ inputMode: 'decimal', className: 'num',
                                        style: { textAlign: 'end' },
                                        'aria-label': t('inv.costOnLine', { n: idx + 1 }) }}
                          onChange={(e) => setLine(l.key, { unit_cost: sanitize(e.target.value) })} />
                      </td>
                      <td className="num">{l.item_code ? fmtMoney(lineValue(l).toFixed(2)) : ''}</td>
                      <td>
                        {l.item_code && (
                          <IconButton size="small" aria-label={t('inv.removeLine')}
                            onClick={() => { setSaved(false); setLines((ls) => ls.filter((x) => x.key !== l.key)) }}>
                            <DeleteIcon fontSize="small" /></IconButton>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
              <tfoot><tr>
                <td colSpan={4}>{t('inv.storeTotal', { count: filled.length })}</td>
                <td className="num">{fmtMoney(total.toFixed(2))}</td><td />
              </tr></tfoot>
            </table>
          </Box>
        )}

        <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 2.5, py: 2 }}>
          <Button size="small" startIcon={<AddIcon />}
            onClick={() => setLines((ls) => [...ls, blank()])}>{t('inv.addLine')}</Button>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" disabled={!dirty || save.isPending || storeId === null}
            onClick={() => save.mutate()}>{t('inv.saveOpening')}</Button>
        </Stack>
      </Paper>

      {o && o.total_value !== undefined && (
        <Paper variant="outlined">
          <Box sx={{ px: 2.5, pt: 2.25 }}>
            <Typography component="h2" sx={{ m: 0, fontSize: 15, fontWeight: 600 }}>
              {t('inv.books')}</Typography>
            <Typography sx={{ fontSize: 13, color: c.inkMuted, mt: 0.25, mb: 1.5 }}>
              {t('inv.booksSub')}</Typography>
          </Box>
          <FigureGrid>
            <Figure label={bi('inv.openingAllStores')} value={fmtMoney(o.total_value)} />
            <Figure label={bi('inv.inTheBooks')}
              value={o.books_value != null ? fmtMoney(o.books_value) : '—'} />
            <Figure label={bi('inv.difference')} value={fmtMoney(diff.toFixed(2))}
              tone={Math.abs(diff) >= 0.01 ? 'warn' : undefined} />
          </FigureGrid>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}
            sx={{ px: 2.5, pb: 2.25 }}>
            <Typography sx={{ fontSize: 13, color: o.books_problem ? c.dangerInk : c.inkSecondary, flex: 1 }}>
              {o.books_problem ?? (Math.abs(diff) < 0.01 ? t('inv.booksMatch') : t('inv.booksDiffer'))}
            </Typography>
            {mayPost && Math.abs(diff) >= 0.01 && !o.books_problem && (
              <Button variant="contained" disabled={dirty || post.isPending}
                onClick={() => setConfirmPost(true)}>{t('inv.postToBooks')}</Button>
            )}
          </Stack>
        </Paper>
      )}

      <Dialog open={confirmPost} onClose={() => setConfirmPost(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('inv.postTitle')}</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14 }}>{t('inv.postBody', {
            value: fmtMoney(o?.total_value ?? '0') })}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmPost(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" disabled={post.isPending} onClick={() => post.mutate()}>
            {t('inv.postToBooks')}</Button>
        </DialogActions>
      </Dialog>

      {importing && storeId !== null && (
        <ImportDialog title={t('inv.importOpeningTitle')} help={t('inv.importOpeningHelp')}
          sample={'code,qty,unit,cost\nPEP330,10,CTN,96.00\nWAT600,240,PCS,0.65'}
          run={(f, dry) => importOpeningStock(storeId, f, dry)}
          invalidate={['inv-opening', 'inv-items', 'inv-stores']}
          onClose={() => setImporting(false)} />
      )}
    </Stack>
  )
}

const strip = (l: Line) => [l.item_code, Number(l.qty), l.unit, Number(l.unit_cost)]
